import { DatePicker, LabelComponent, Stack, TimePicker } from 'components';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import Popover from '@mui/material/Popover';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import style from './dateAndTime.module.scss';

dayjs.extend(customParseFormat);

type Props = {
  hasLabel?: boolean;
  dateValue: string | null;
  datePickerStyles?: string;
  onChangeDate: (date: string) => void;
  onBlurDate: () => void;
  timeValue: string | null;
  timePickerStyles?: string;
  onChangeTime: (time: string) => void;
  onBlurTime: () => void;
};

const DateAndTime = ({
  hasLabel,
  dateValue,
  datePickerStyles,
  onChangeDate,
  onBlurDate,
  timeValue,
  timePickerStyles,
  onChangeTime,
  onBlurTime
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localTime, setLocalTime] = useState<React.ReactNode | null>();
  const [localStartDate, setLocalDate] = useState<React.ReactNode | null>();
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (dateValue && dateValue !== 'Invalid Date' && dayjs(dateValue).isValid()) {
      setLocalDate(dayjs(dateValue).format('MM/DD/YYYY'));
    }

    if (!dateValue) {
      setLocalDate(null);
    }
  }, [dateValue]);

  useEffect(() => {
    if (timeValue && timeValue !== 'Invalid Date' && dayjs(timeValue, 'HH:mm:ss', true).isValid()) {
      setLocalTime(dayjs(timeValue, 'HH:mm:ss').format('hh:mm A'));
    }

    if (!timeValue) {
      setLocalTime(null);
    }
  }, [timeValue]);

  return (
    <div>
      <Stack width="100%">
        {hasLabel && <LabelComponent text="Date and Time" />}
        <button onClick={handleClick} className={style.popoverButton} type="button">
          {(!dateValue || dateValue === 'Invalid Date') && !timeValue && (
            <div className={style.placeholderInsidePopover}>Select</div>
          )}
          <div className={style.valueInsidePopover}>
            {localStartDate}
            {localTime && localStartDate && <div className={style.separator}>|</div>}
            {localTime}
          </div>
        </button>
      </Stack>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow:
              '1px 1px 4px 0px rgba(80, 117, 177, 0.20), 4px 4px 25px 0px rgba(80, 117, 177, 0.15)',
            overflow: 'hidden'
          }
        }}
        anchorReference="anchorPosition"
        anchorPosition={{
          top: Number(anchorEl && anchorEl?.getBoundingClientRect()?.bottom + 6),
          left: Number(anchorEl?.getBoundingClientRect()?.left) - 0.4
        }}
      >
        <div className={style.popoverContainer}>
          <Stack width="100%">
            <LabelComponent text="Date" />
            <DatePicker
              classnamesProps={datePickerStyles}
              onChange={onChangeDate}
              onBlur={onBlurDate}
              value={dateValue}
              placeholder="YYYY-MM-DD"
            />
          </Stack>
          <Stack width="100%">
            <LabelComponent text="Time" />
            <TimePicker
              classnamesProps={timePickerStyles}
              onChange={onChangeTime}
              onBlur={onBlurTime}
              value={timeValue}
            />
          </Stack>
        </div>
      </Popover>
    </div>
  );
};

export default DateAndTime;
